import { useState, useCallback, useEffect } from 'react';
import { validateForm, validateField } from '../schemas/validation.schemas';

/**
 * Custom hook for form management with validation
 */
export const useForm = (initialValues = {}, validationSchema = {}, options = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const {
    validateOnChange = true,
    validateOnBlur = true,
    resetOnSubmit = false
  } = options;

  // Update form values
  const setValue = useCallback((name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    
    // Validate on change if enabled
    if (validateOnChange && validationSchema[name]) {
      const fieldErrors = validateField(value, validationSchema[name], values);
      if (fieldErrors.length > 0) {
        setErrors(prev => ({ ...prev, [name]: fieldErrors }));
      }
    }
  }, [errors, values, validateOnChange, validationSchema]);

  // Handle input change
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    setValue(name, fieldValue);
  }, [setValue]);

  // Handle input blur
  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Validate on blur if enabled
    if (validateOnBlur && validationSchema[name]) {
      const fieldErrors = validateField(values[name], validationSchema[name], values);
      setErrors(prev => ({ ...prev, [name]: fieldErrors.length > 0 ? fieldErrors : null }));
    }
  }, [values, validateOnBlur, validationSchema]);

  // Validate entire form
  const validate = useCallback(() => {
    const { isValid, errors: formErrors } = validateForm(values, validationSchema);
    setErrors(formErrors);
    return isValid;
  }, [values, validationSchema]);

  // Reset form
  const reset = useCallback((newValues = initialValues) => {
    setValues(newValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  // Submit form
  const handleSubmit = useCallback((onSubmit) => {
    return async (e) => {
      if (e) {
        e.preventDefault();
      }
      
      setIsSubmitting(true);
      
      try {
        const isValid = validate();
        
        if (isValid) {
          await onSubmit(values);
          
          if (resetOnSubmit) {
            reset();
          }
        }
      } catch (error) {
        console.error('Form submission error:', error);
      } finally {
        setIsSubmitting(false);
      }
    };
  }, [values, validate, reset, resetOnSubmit]);

  // Get field props for easy binding
  const getFieldProps = useCallback((name) => ({
    name,
    value: values[name] || '',
    onChange: handleChange,
    onBlur: handleBlur,
    error: touched[name] && errors[name],
    hasError: touched[name] && !!errors[name]
  }), [values, handleChange, handleBlur, touched, errors]);

  // Check if form is valid
  const isValid = Object.keys(errors).every(key => !errors[key] || errors[key].length === 0);
  
  // Check if form has been modified
  const isDirty = JSON.stringify(values) !== JSON.stringify(initialValues);

  return {
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    isDirty,
    setValue,
    handleChange,
    handleBlur,
    handleSubmit,
    validate,
    reset,
    getFieldProps
  };
};

/**
 * Hook for multi-step forms
 */
export const useMultiStepForm = (steps = [], initialValues = {}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialValues);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const nextStep = useCallback(() => {
    if (!isLastStep) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(prev => prev + 1);
    }
  }, [isLastStep, currentStep]);

  const prevStep = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  }, [isFirstStep]);

  const goToStep = useCallback((step) => {
    if (step >= 0 && step < totalSteps) {
      setCurrentStep(step);
    }
  }, [totalSteps]);

  const updateFormData = useCallback((stepData) => {
    setFormData(prev => ({ ...prev, ...stepData }));
  }, []);

  const resetForm = useCallback(() => {
    setCurrentStep(0);
    setFormData(initialValues);
    setCompletedSteps(new Set());
  }, [initialValues]);

  const currentStepData = steps[currentStep] || {};

  return {
    currentStep,
    currentStepData,
    formData,
    isFirstStep,
    isLastStep,
    totalSteps,
    progress,
    completedSteps,
    nextStep,
    prevStep,
    goToStep,
    updateFormData,
    resetForm
  };
};

export default useForm;